import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useEffect, useMemo, useRef } from "react";
import {
  PRICE_DETAIL_MIN_HEIGHT,
  priceDetailGridStyles,
  returnPlaceholderProviderRows,
  returnPriceDetailCols,
} from "./helper/priceTableHelper";

import {
  getFontSizePx,
  getHeaderHeight,
  getRowHeight,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import { reapplyDetailRowHeight } from "@/lib/agGridDetailRowHeightHelper";
import priceDataStore from "@/store/priceDataStore";
import useTableUIStore from "@/store/tableUIStore";
import { useTheme } from "next-themes";

const PriceTableDetail = ({ data, node, api }: CustomCellRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const priceStoreState = priceDataStore((state) => state.data);
  const { theme } = useTheme(); // cant pass into the table or too many rerenders

  const colDefs = useMemo(() => returnPriceDetailCols(), []);

  //Odd row stripe
  const tableStripeOddTrue = useTableUIStore(
    (state) => state.tableStripePricesOn,
  ); // used to change the table stripe with settings.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeightSetting = useTableUIStore((state) => state.rowHeight);
  const dynamicRowHeight = useMemo(
    () => getRowHeight(rowHeightSetting),
    [rowHeightSetting],
  );
  const dynamicHeaderHeight = useMemo(
    () => getHeaderHeight(rowHeightSetting),
    [rowHeightSetting],
  );

  const providerPrices = priceStoreState.find(
    (priceRow) => priceRow.isin == data.isin,
  )?.providerPrices;

  // No endpoint serves provider prices yet, so the dropdown shows the expected
  // providers with empty price cells. Once one exists, fetch it for this ISIN here
  // and write the result onto the row (priceDataStore.updateSinglePrice) - real rows
  // then replace the placeholder without any change to this grid.
  const rowData = useMemo(
    () =>
      providerPrices && providerPrices.length > 0
        ? providerPrices
        : returnPlaceholderProviderRows(),
    [providerPrices],
  );

  // Flipping the theme resets AG Grid's row heights, collapsing this expanded row to
  // 1px. Push the measured height back on so it survives the switch.
  useEffect(
    () => reapplyDetailRowHeight(node, api, containerRef.current?.clientHeight),
    [theme, rowData.length, dynamicRowHeight, node, api],
  );

  const defaultColDef = useMemo(() => {
    return {
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      suppressColumnStateEvents: true,
      cellStyle: { borderRight: "Var(--ag-grid-cubeCustom-cell-border)" },
    };
  }, []);

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  return (
    <div
      data-testid="PriceTableDetailContainer"
      id="PriceTableDetailContainer"
      style={{ minHeight: `${PRICE_DETAIL_MIN_HEIGHT}px` }}
      className="flex w-full flex-row bg-cubeBase-500 dark:bg-cubeBaseDark-500"
      ref={containerRef}
    >
      {/* flex-1 rather than h-full/w-full: the shared nested-table margin has to come
          out of the size, not be added to it, or the grid overflows the row and the
          scrollbar and rounded corners end up underneath the next row. */}
      <div
        className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} cssStylingOnNestedTable1 min-w-0 flex-1`}
        style={{
          ...(priceDetailGridStyles(
            theme,
            tableStripeOddTrue,
          ) as React.CSSProperties),
          fontSize: `${getFontSizePx(fontSize)}px`,
        }}
      >
        <AgGridReact
          data-id="detailGrid"
          columnDefs={colDefs}
          rowData={rowData}
          tooltipShowDelay={0}
          defaultColDef={defaultColDef}
          rowHeight={dynamicRowHeight}
          headerHeight={dynamicHeaderHeight}
          // Same as the blotter's nested tables: AG Grid sizes itself to its rows plus
          // the horizontal scrollbar, so nothing has to guess the height.
          domLayout="autoHeight"
          scrollbarWidth={6} // Matches the master grid - the default bar is chunky.
          popupParent={popupParent}
        />
      </div>
    </div>
  );
};

export default PriceTableDetail;
